<template>
  <BaseDialog
    :visible="open"
    @update:open="(val) => emit('update:open', val)"
    :width="dialogWidth"
    :draggable="false"
    :dialogClass="windowWidth < 768 ? 'mobile-dialog' : ''"
    @keydown.esc="emit('update:open', false)"
    role="dialog"
    aria-modal="true"
    aria-labelledby="lose-data-title"
  >
    <template #header>
      <!-- Mobile: back + close row, title below -->
      <div v-if="windowWidth < 768" class="w-full pt-5 px-5 pb-5">
        <div class="flex justify-between items-center">
          <button
            @click="onBack"
            @keydown.enter="onBack"
            class="cursor-pointer border-none bg-transparent p-2 -ml-1"
            v-html="backSvg"
            tabindex="0"
            :aria-label="$t('accessibilityLabels.goBack')"
          ></button>
          <button
            @click="$emit('update:open', false)"
            @keydown.enter="$emit('update:open', false)"
            class="cursor-pointer border-none bg-transparent p-2 flex items-center mr-[4px] mt-[4.74px]"
            v-html="cancelSvg"
            tabindex="0"
            :aria-label="$t('accessibilityLabels.closeDialog')"
            :autofocus="visible ? true : false"
          ></button>
        </div>
        <div class="mt-2">
          <h2
            id="lose-data-title"
            class="!text-[24px] md:!text-[30.72px] !font-just font-normal text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2"
          >
            <span v-html="IconPassphrase"></span>
            {{ $t('securityModule.dialog.loseData.title') }}
        </h2>
        </div>
      </div>
      <!-- Desktop: original layout -->
      <div v-else class="flex justify-between items-center p-6 w-full">
        <button
          v-if="windowWidth < 768"
          @click="$emit('update:open', false)"
          @keydown.enter="$emit('update:open', false)"
          class="cursor-pointer border-none bg-transparent p-2 flex items-center"
          v-html="backSvg"
          tabindex="0"
          :aria-label="$t('accessibilityLabels.goBack')"
        ></button>
        <h2
          id="lose-data-title"
          class="!text-[24px] md:!text-[30.72px] !font-just font-normal text-btnActive !tracking-[0%] !leading-[100%] flex items-center gap-2"
        >
          <span v-html="IconPassphrase"></span>
          {{ $t('securityModule.dialog.loseData.title') }}
      </h2>
        <button
          @click="$emit('update:open', false)"
          @keydown.enter="$emit('update:open', false)"
          class="cursor-pointer border-none bg-transparent p-2 flex items-center"
          v-html="cancelSvg"
          tabindex="0"
          :aria-label="$t('accessibilityLabels.closeDialog')"
        ></button>
      </div>
    </template>
    <div class="bg-white px-5 pb-5 rounded-[23.04px] font-just">
      <div class="mb-6">
        <div class="border-[1px] border-solid border-whiteBlue"></div>
      </div>
      <div class="flex flex-col items-start justify-center gap-4">
        <span class="flex items-center gap-4" role="alert" aria-live="assertive">
          <span v-html="alertCircleSvg()"></span>
          <p
            class="text-[20px] md:text-[24px] font-just font-[500] text-alert tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.loseData.warning')"
          ></p
        ></span>

        <div>
          <span
            class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.loseData.subtitle')"
          ></span>
          <div class="flex items-start justify-start flex-col gap-2 mt-2">
            <div class="flex items-center gap-2">
              <span v-html="carouselRightArrowSvg"></span>
              <span
                class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
                v-html="
                  formatReadableSize(
                      securityStore.encryptionStatistics?.dataSize || 0
                    ) + ' ' + $t('securityModule.dialog.loseData.size')
                "
              ></span>
            </div>
            <div class="flex items-center gap-2">
              <span v-html="carouselRightArrowSvg"></span>
              <span
                class="text-[16px] md:text-[20px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
                v-html="
                  $t('securityModule.dialog.loseData.reAuth').replace('agent(s)', securityStore.encryptionStatistics?.agents || 0 + ' agent(s)')
                "
              ></span>
            </div>
          </div>
        </div>

        <div class="flex flex-col items-start gap-4 mb-2 mt-8 w-full">
          <span
            class="text-[20px] md:text-[24px] font-just font-normal text-btnActive tracking-[0%] leading-[100%]"
            v-html="$t('securityModule.dialog.loseData.confirm')"
          ></span>
          <BaseInput
            ref="confirmationInput"
            id="lose-passphrase-input"
            v-model="passphrase"
            :label="$t('securityModule.dialog.loseData.passphraseTitle')"
            :labelClass="'!text-grayOverlay/60 !text-[14px]'"
            :labelState="false"
            :rows="4"
            :textarea="true"
            class="w-full !border-lightGray"
            auto-grow
            aria-describedby="confirmation-help"
            aria-required="true"
          />
          <span id="confirmation-help" class="sr-only">Type 'lose my encrypted data' to confirm</span>
        </div>
      </div>
    </div>
    <div v-if="securityStore.errorMsg" role="alert" aria-live="assertive">
      <span
        class="text-base my-2 !font-just text-error flex justify-center"
        data-test-id="lose-data-errorMessage"
      >
        {{ securityStore.errorMsg }}
      </span>
    </div>
    <template #footer>
      <div class="w-full p-6 px-5 pb-5 pt-5">
        <BaseButton
          class="dialog-btn dialog-btn--accept w-full !bg-btnActive disabled:!bg-grayOverlay"
          :label="$t('securityModule.dialog.loseData.loseMyDataBtn')"
          :disabled="
            passphrase.toLowerCase() !== 'lose my encrypted data' ||
            securityStore.loading
          "
          @click="onSubmit"
          :loading="securityStore.loading"
        />
      </div>
    </template>
  </BaseDialog>
  <!-- Nested restore passphrase popup on back -->
  <RestorePassphraseDialog
    v-if="showRestorePassphraseDialog"
    v-model:open="showRestorePassphraseDialog"
  />
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import BaseDialog from "@/components/Base/BaseDialog.vue";
import RestorePassphraseDialog from "@/views/Security/dialogs/RestorePassphraseDialog.vue";
import BaseInput from "@/components/Base/BaseInput.vue";
import BaseButton from "@/components/Base/BaseButton.vue";
import {
  alertCircleSvg,
  carouselRightArrowSvg,
  closeSvg,
  IconPassphrase,
  backArrowSvg
} from "@/utils/commonSvg";
import { useSecurityStore } from "@/stores/securityStore";
import { formatReadableSize } from "@/utils/common";
import { useI18n } from "vue-i18n";

// Access the global security store instances
const securityStore = useSecurityStore();
const { t } = useI18n();

// Component props definition
const props = defineProps({
  // Controls the visibility of the dialog
  open: {
    type: Boolean,
    required: true,
  },
});

// Emits custom events to notify parent about dialog state and asset actions
// - "update:open": Dialog visibility change
// - "submit": Delete encrypted data
const emit = defineEmits(["update:open", "submit"]);

// State management
const encrypting = ref(false);
const windowWidth = ref(window.innerWidth);
const passphrase = ref("");
const showRestorePassphraseDialog = ref(false);
const confirmationInput = ref(null);
const hint = ref("");

// Computed properties for UI elements
const cancelSvg = computed(() => closeSvg({ width: "24px", height: "24px" }));
const backSvg = computed(() =>
  backArrowSvg({ width: "24", height: "24px", color: "#818181" })
);

// Responsive dialog width (full width on mobile, 50% on desktop)
const dialogWidth = computed(() => {
  return windowWidth.value < 768 ? "100%" : "50%";
});

/**
 * Handle form submission
 * Emits the 'submit' event with the current passphrase value
 */
function onSubmit() {
  emit("submit", passphrase.value);
}

// Open the restore passphrase dialog when back is tapped on mobile
function onBack() {
  showRestorePassphraseDialog.value = true;
}

/**
 * Update window dimensions for responsive behavior
 */
function handleResize() {
  windowWidth.value = window.innerWidth;
}

/**
 * Fetches encryption status and hint from the security store
 * Only makes the API call if the hint is not already available
 */
const getEncryption = async () => {
  if (!securityStore.encryptionDetails?.hint) {
    encrypting.value = true;

    try {
      await securityStore.getEncryptionStatus();
      hint.value = securityStore.encryptionDetails?.hint;
    } catch (error) {
      console.error("Error fetching encryption status:", error);
    } finally {
      encrypting.value = false;
    }
  }
};

// Lifecycle hooks
onMounted(() => {
  // Initialize component when mounted
  getEncryption();
  window.addEventListener("resize", handleResize);
  // Auto-focus confirmation input when dialog opens
  setTimeout(() => {
    if (confirmationInput.value) {
      confirmationInput.value.$el?.querySelector('textarea')?.focus();
    }
  }, 100);
});

onBeforeUnmount(() => {
  // Clean up event listeners when component is unmounted
  window.removeEventListener("resize", handleResize);
});
</script>

<style>
.mobile-dialog {
  height: 100% !important;
  max-height: 100% !important;
}
</style>
